import React, { Fragment, useState } from 'react'
import { Row, Col } from 'react-flexbox-grid'
import { useTranslation } from 'react-i18next'
import get from 'lodash/get'

import { ContentBlock, ExpandableBlocks } from 'components/ContentBlock'
import FixProdsDetails from './FixProdsDetails'
import Button from 'components/Button'
import { Form, Number, Select, Checkbox, WeekdayPicker } from 'components/forms'
import { WarningModal, ConfirmModal } from 'components/Modal'
import Divider from 'components/Divider'
import listCategories from 'sections/category/gql/listCategories'
import queryPriceLevels from 'config/localServer/query/priceLevelsQuery'
import { minMaxFields } from 'helpers/validators'

const SetDetails = ({ setData = {}, onChangeSetData, onSelectCategory }) => {
	const [categoryName, setCategoryName] = useState(get(setData, 'category.name'))
	const [showWarningModal, setShowWarningModal] = useState(false)
	const [t] = useTranslation()

	const isNewItem = !setData.category
	const handleDataChange = (vals, isSubmit) => {
		const isUpdating = setData && setData.category
		const { setInd } = setData
		if(isUpdating || isSubmit) {
			// 'setInd' has not field in Form, so it's value isn't stored and we need add it manually
			onChangeSetData({ ...vals, ...(setInd || setInd === 0) && { setInd } }, isSubmit)
		}
	}
	return (
		<ContentBlock related label={categoryName || t('product_section.strings.new_set')}>
			<Form
				initialValues={{ weekDays: 0, ...setData }}
				onSubmit={vals => handleDataChange(vals, isNewItem)}
				smartSubmit={!isNewItem}
				// reinitByModVer={false}
			>
				{({ formState, formApi }) => {
					const catId = get(formState.values, 'category.id'),
						{ fixProducts = [] } = formState.values
					const handleChangeCategory = ({ id, name }, label) => {
						const isAlreadyExists = onSelectCategory(id)
						const oldValue = formState.values.category
						const changeCategory = () => {
							formApi.setValue('fixProducts', [])
							setCategoryName(label)
							return { id, name }
						}
						const curCat = formApi.getValue('category')
						if(isAlreadyExists) {
							setShowWarningModal(true)
							return oldValue || null
						}
						// Change category if fixed products isn't chosen
						if(!fixProducts.length) {
							return changeCategory()
						}
						// Warning of deleting chosen fixed products
						return ConfirmModal({ text: t('product_section.strings.fixed_products_clear_warning') })
							.then(
								confirmed => changeCategory(),
								rejected => curCat
							)
					}

					return(
						<Fragment>
							<ExpandableBlocks nested>
								<ContentBlock label={t('product_section.strings.general_information')} startExpanded>
									<Row>
										<Col xs>
											<Select
												field='category'
												valueField='id'
												labelField='name'
												withVirtualBody
												isSearchable
												remoteOptions={{ query: listCategories, variables: { params: { limit: 50 } } }}
												beforeSetFormValue={handleChangeCategory}
												label={t('category_section.caption')}
												simpleValue={false} />
											<div>
												<Select
													inline
													field='priceLevel'
													remoteOptions={{
														query: queryPriceLevels,
														modifyOptions: options => {
															const selectOptions = options.map((item, i) => ({ value: i + 1, label: item }))
															return [...selectOptions, { value: 99, label: t('product_section.strings.price_level_null') }]
														}
													}}
													label={t('product_section.fields.price_level')}
												/>
												<Checkbox inline field='isCollectPoints' label={t('product_section.strings.no_points')}/>
											</div>
										</Col>
										<Col xs>
											<div>
												<Number
													field='minQuantity'
													label={t('product_section.strings.min_quantity')}
													{...minMaxFields('min', 'maxQuantity')}
													placeholder='-'
													shrink
													withControls={false}
													inline />
												<Number
													field='maxQuantity'
													label={t('product_section.strings.max_quantity')}
													{...minMaxFields('max', 'minQuantity')}
													shrink
													withControls={false}
													inline />
											</div>
											<Select
												field='course'
												isClearable
												label={t('product_section.fields.course')}
												options={[{
													value: '1',
													label: t('product_section.fields.course', { num: '1' })
												}, {
													value: '2',
													label: t('product_section.fields.course', { num: '2' })
												}, {
													value: '3',
													label: t('product_section.fields.course', { num: '3' })
												}, {
													value: '4',
													label: t('product_section.fields.course', { num: '4' })
												}, {
													value: '5',
													label: t('product_section.fields.course', { num: '5' })
												}, {
													value: '6',
													label: t('product_section.fields.course', { num: '6' })
												}, {
													value: '7',
													label: t('product_section.fields.course', { num: '7' })
												}, {
													value: '8',
													label: t('product_section.fields.course', { num: '8' })
												}]}
											/>
										</Col>
										<Col xs>
											<WeekdayPicker field='weekDays' label={t('common.fields.week_days')}/>
										</Col>
									</Row>
								</ContentBlock>
								<ContentBlock label={t('product_section.strings.fixed_products')}>
									<FixProdsDetails field='fixProducts' selectedCategory={catId} {...!isNewItem && { onFieldChange: formApi.submitForm }} />
								</ContentBlock>
							</ExpandableBlocks>
							{!setData.category && // Render save button only when adding new record
								<Fragment>
									<Divider />
									<Button status={catId ? 'primary' : 'inactive'} type='submit' text={t('common.buttons.apply')} />
								</Fragment>
							}
							{showWarningModal &&
								<WarningModal
									text={t('validators.duplicate')}
									onClose={() => setShowWarningModal(false)} />
							}
						</Fragment>
					)
				}}
			</Form>
		</ContentBlock>
	)
}

export default SetDetails
